import { FormControlProps } from "@chakra-ui/form-control";
import {
  Box,
  BoxProps,
  FormControl,
  RadioGroup,
  SystemStyleObject,
  Text,
  Tooltip,
  TooltipProps,
  useMultiStyleConfig,
  useRadio,
  useRadioGroupContext,
} from "@chakra-ui/react";
import { forwardRef } from "@chakra-ui/system";
import { dataAttr } from "@chakra-ui/utils";
import React from "react";
import { useController, useFormState } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ButtonOrder } from "shared/components/button/ButtonOrder";
import { usePrefixedFieldName } from "shared/components/form/FormFieldPrefix";
import { useIsFormDisabled } from "shared/components/form/FormStateProvider";
import { InputMessages } from "shared/components/form/InputContent";
import { ErrorTooltip } from "shared/components/tooltip/ErrorTooltip";
import { InfoCircleIcon } from "shared/icons/InfoCircleIcon";
import { getFieldError } from "shared/utils/FormUtils";

export interface ToggleButtonGroupProps
  extends Omit<BoxProps, "onChange" | "title"> {
  title?: React.ReactNode;
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  error?: string;
  variant?: "rounded" | "timeframe";
  size?: "sm" | "md" | "lg";
  containerStyles?: SystemStyleObject;
  controlProps?: FormControlProps;
  showMessages?: boolean;
  isDisabled?: boolean;
  isRequired?: boolean;
}

interface ToggleButtonProps {
  value: string;
}

export const ToggleButtonGroup = React.forwardRef<
  HTMLDivElement,
  React.PropsWithChildren<ToggleButtonGroupProps>
>(
  (
    {
      value,
      defaultValue,
      error,
      onChange,
      children,
      title,
      variant,
      size,
      containerStyles,
      controlProps,
      showMessages = true,
      isDisabled,
      isRequired,
      ...props
    },
    ref,
  ) => {
    const styles = useMultiStyleConfig("ToggleButton", { variant, size });
    const content = (
      <Box
        sx={styles.groupContainer}
        __css={containerStyles}
        data-disabled={dataAttr(isDisabled)}
        {...props}
      >
        {title && <Text mb={1}>{title}</Text>}
        <FormControl
          isInvalid={!!error}
          isRequired={isRequired}
          isDisabled={isDisabled}
          userSelect="none"
          data-invalid={!!error}
          data-indeterminate={dataAttr(!value)}
          sx={styles.group}
          {...controlProps}
        >
          <RadioGroup
            ref={ref}
            display="flex"
            value={value}
            onChange={onChange}
            variant={variant}
            size={size}
            isDisabled={isDisabled}
            defaultValue={defaultValue}
          >
            {children}
          </RadioGroup>
          {showMessages && <InputMessages error={error} />}
        </FormControl>
      </Box>
    );
    return showMessages ? (
      content
    ) : (
      <ErrorTooltip error={error}>{content}</ErrorTooltip>
    );
  },
);
ToggleButtonGroup.displayName = "ToggleButtonGroup";

export const ToggleButton = forwardRef<ToggleButtonProps, "button">(
  (props, ref) => {
    const group = useRadioGroupContext();

    const isChecked = group.value === props.value;
    const isDisabled = group.isDisabled;

    const { getInputProps, getRootProps, getRadioProps, state } = useRadio({
      ...props,
      isChecked,
      name: group.name,
      onChange: group.onChange,
    });

    const { onClick, onKeyDown, ...rootProps } = getRootProps();
    const inputProps = getInputProps({}, ref);
    const radioProps = getRadioProps();

    const isIndeterminate = !group.value;

    const styles = useMultiStyleConfig("ToggleButton", { ...group, ...props });

    const handleOnClick = (e: React.MouseEvent) => {
      if (isDisabled) {
        e.preventDefault();
        return;
      }
      if (isChecked) {
        e.preventDefault();
        if (!state.isRequired) {
          group.onChange("");
        }
      } else {
        onClick?.(e as never);
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (isDisabled) {
        e.preventDefault();
        return;
      }
      if (e.key === " " && isChecked) {
        group.onChange("");
        e.preventDefault();
      } else {
        onKeyDown?.(e as never);
      }
    };

    return (
      <Box
        {...rootProps}
        __css={styles.container}
        as="label"
        onClick={handleOnClick}
        onKeyDown={handleKeyDown}
        data-disabled={dataAttr(isDisabled)}
      >
        <input {...inputProps} disabled={isDisabled} />
        <Box
          {...radioProps}
          __css={styles.control}
          data-indeterminate={isIndeterminate ? "1" : undefined}
        >
          {props.children}
        </Box>
      </Box>
    );
  },
);

ToggleButton.displayName = "ToggleButton";

export interface FormToggleButtonGroupProps
  extends Omit<ToggleButtonGroupProps, "value" | "onChange"> {
  name: string;
  shouldUnregister?: boolean;
}

export const FormToggleButtonGroup: React.FC<
  React.PropsWithChildren<FormToggleButtonGroupProps>
> = ({ name, isDisabled, shouldUnregister = true, ...otherProps }) => {
  const prefixedName = usePrefixedFieldName(name);
  const { field } = useController({
    name: prefixedName,
    shouldUnregister,
  });
  const { errors } = useFormState();
  const error = getFieldError(errors, prefixedName)?.message;
  const isFormDisabled = useIsFormDisabled();
  return (
    <ToggleButtonGroup
      value={field.value as string}
      onChange={field.onChange}
      error={error}
      isDisabled={isDisabled || isFormDisabled}
      {...otherProps}
    />
  );
};

export type BooleanToggleButtonGroupValue = boolean | "";

export interface BooleanToggleButtonGroupProps
  extends Omit<ToggleButtonGroupProps, "value" | "onChange"> {
  trueText?: string;
  falseText?: string;
  value?: BooleanToggleButtonGroupValue;
  onChange: (value: BooleanToggleButtonGroupValue) => void;
}

export interface FormBooleanToggleButtonGroupProps
  extends FormToggleButtonGroupProps {
  trueText?: string;
  falseText?: string;
}

export const BooleanToggleButtonGroup = React.forwardRef<
  HTMLDivElement,
  BooleanToggleButtonGroupProps
>(({ trueText, falseText, value, onChange, ...props }, ref) => {
  const { t } = useTranslation(["common"]);
  return (
    <ToggleButtonGroup
      ref={ref}
      value={value?.toString() ?? undefined}
      onChange={(value) => onChange(value === "" ? "" : value === "true")}
      {...props}
    >
      <ButtonOrder
        confirm={
          <ToggleButton value="true">
            {trueText || t("common:yes")}
          </ToggleButton>
        }
        dismiss={
          <ToggleButton value="false">
            {falseText || t("common:no")}
          </ToggleButton>
        }
      />
    </ToggleButtonGroup>
  );
});
BooleanToggleButtonGroup.displayName = "BooleanToggleButtonGroup";

export const FormBooleanToggleButtonGroup: React.FC<
  FormBooleanToggleButtonGroupProps
> = ({ name, isDisabled, ...otherProps }) => {
  const prefixedName = usePrefixedFieldName(name);
  const {
    field,
    formState: { errors },
  } = useController<{
    selected?: BooleanToggleButtonGroupValue;
  }>({
    name: prefixedName as "selected",
    shouldUnregister: true,
  });
  const isFormDisabled = useIsFormDisabled();
  const error = getFieldError(errors, prefixedName)?.message;
  return (
    <BooleanToggleButtonGroup
      isDisabled={isDisabled || isFormDisabled}
      error={error}
      {...field}
      {...otherProps}
    />
  );
};

export const ToggleButtonTooltip: React.FC<Partial<TooltipProps>> = (props) => (
  <Tooltip {...props}>
    <InfoCircleIcon w="10px" h="10px" color="gray.400" />
  </Tooltip>
);
